import {
  trackEvent,
  type FunnelEventPayload,
} from 'components/shared/UserAnalytics/funnelEvents';
import { flushAnalyticsQueue } from 'lib/analytics/fanout';
import { allowsDataLayer } from 'lib/consent/gate';
import { sampleAvailabilityActive } from './config';

/** Every parameter a Best Buy pickup event can carry. GTM keeps the last
 * value of each dataLayer key for the life of the page, so an event that
 * does not send a key would otherwise report the previous event's value
 * (a success result carrying the last failure's `reason`, a checkout
 * carrying the home hero's `entry`). */
export const BB_PICKUP_PARAM_KEYS = [
  'entry',
  'sku_mode',
  // The 3-digit ZIP area, never a full ZIP. Named without "zip": the
  // fan-out (lib/analytics/fanout.ts) drops any key that looks like one.
  'area_prefix',
  'location_id',
  'sku',
  'available',
  'reason',
  'reason_detail',
  'store_count',
  'change',
  'granted',
  'removed_count',
  'trigger',
  'sample',
  'order_reference',
] as const;

/** Resets the pickup keys in GTM's data model. A push without `event`
 * fires no trigger and only updates the model; `undefined` clears a key.
 * It never reaches Vercel Analytics, which only gets what each event
 * sends. Skipped where the dataLayer itself is not allowed, as the event
 * push is skipped there too. */
export function flushPickupParams(): void {
  if (typeof window === 'undefined' || !allowsDataLayer()) return;
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer ??= [];
  w.dataLayer.push(
    Object.fromEntries(BB_PICKUP_PARAM_KEYS.map((key) => [key, undefined])),
  );
}

/** `trackEvent` for the pickup events: the model reset first, then the
 * event with exactly the keys it sends. */
export function trackPickupEvent(
  event: string,
  payload: FunnelEventPayload = {},
): void {
  flushPickupParams();
  trackEvent(event, payload);
  // Production defers delivery to idle while the flush above is immediate;
  // drain now so each clear and its event reach the dataLayer in order.
  flushAnalyticsQueue();
}

/** GA4 marker for events produced from sample data (config.ts), so
 * fabricated-availability rows are filterable in the pickup funnel. */
export const sampleTag = (): { sample: true } | Record<string, never> =>
  sampleAvailabilityActive() ? { sample: true } : {};
